import { useEffect } from 'react';
import { useErrorMonitoring } from '@/hooks/useErrorMonitoring';
import { supabase } from '@/integrations/supabase/client';

export const CriticalErrorMonitor = () => {
  const { reportDatabaseError, reportAPIError } = useErrorMonitoring();

  useEffect(() => {
    // Global error handler for unhandled JavaScript errors
    const handleGlobalError = (event: ErrorEvent) => {
      console.error('Global error caught:', event.error);

      // Determine if this is a critical error based on the stack trace or message
      const errorMessage = event.error?.message || event.message;
      const isPaymentError = errorMessage.toLowerCase().includes('payment') ||
        event.filename?.includes('payment') ||
        window.location.pathname.includes('checkout');

      const isAuthError = errorMessage.toLowerCase().includes('auth') ||
        errorMessage.toLowerCase().includes('login') ||
        errorMessage.toLowerCase().includes('signup');

      if (isPaymentError || isAuthError) {
        reportAPIError(event.error || errorMessage, 'global_error', {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          stack: event.error?.stack
        });
      }
    };

    // Global handler for unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);

      // Check if this is a Supabase/database error
      const errorMessage = event.reason?.message || String(event.reason);
      const isSupabaseError = errorMessage.includes('supabase') ||
        errorMessage.includes('postgresql') ||
        errorMessage.includes('connection') ||
        errorMessage.includes('RLS') ||
        errorMessage.includes('permission denied');

      if (isSupabaseError) {
        reportDatabaseError(event.reason, 'unhandled_rejection', {
          reason: event.reason,
          type: 'promise_rejection'
        });
      }
    };

    // Monitor fetch API for failed requests
    const originalFetch = window.fetch;
    window.fetch = async (input, init) => {
      try {
        const response = await originalFetch(input, init);

        // Report failed API calls to critical endpoints
        if (!response.ok) {
          const url = input instanceof Request ? input.url : typeof input === 'string' ? input : input.toString();

          // Skip analytics and tracking services
          const isAnalyticsRequest = url.includes('google-analytics.com') ||
            url.includes('analytics.google.com') ||
            url.includes('googletagmanager.com') ||
            url.includes('facebook.com/tr') ||
            url.includes('doubleclick.net') ||
            url.includes('google.com/ads') ||
            url.includes('googleadservices.com') ||
            url.includes('clarity.ms') ||
            url.includes('bing.com') ||
            url.includes('/gtag/') ||
            url.includes('/ga/');

          if (isAnalyticsRequest) {
            return response;
          }

          const isCriticalEndpoint = url.includes('/functions/') ||
            url.includes('invoice4u') ||
            url.includes('payment') ||
            url.includes('auth');

          if (isCriticalEndpoint) {
            reportAPIError(
              `HTTP ${response.status}: ${response.statusText}`,
              'fetch_error',
              {
                url,
                status: response.status,
                statusText: response.statusText,
                method: init?.method || 'GET'
              }
            );
          }
        }

        return response;
      } catch (error) {
        const url = input instanceof Request ? input.url : typeof input === 'string' ? input : input.toString();

        // Skip analytics and tracking services for errors too
        const isAnalyticsRequest = url.includes('google-analytics.com') ||
          url.includes('analytics.google.com') ||
          url.includes('googletagmanager.com') ||
          url.includes('facebook.com/tr') ||
          url.includes('doubleclick.net') ||
          url.includes('google.com/ads') ||
          url.includes('googleadservices.com') ||
          url.includes('clarity.ms') ||
          url.includes('bing.com') ||
          url.includes('/gtag/') ||
          url.includes('/ga/');

        if (!isAnalyticsRequest) {
          reportAPIError(error as Error, 'fetch_exception', {
            url,
            method: init?.method || 'GET'
          });
        }
        throw error;
      }
    };

    // Monitor Supabase client errors - simplified approach
    const monitorSupabaseErrors = () => {
      // We'll monitor errors through global error handlers instead of overriding methods
      // This prevents breaking the Supabase query builder pattern
      if (import.meta.env.DEV) {
        console.log('Supabase error monitoring initialized');
      }
    };

    // Set up monitoring
    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    monitorSupabaseErrors();

    // Cleanup
    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.fetch = originalFetch;
    };
  }, [reportDatabaseError, reportAPIError]);

  // This component doesn't render anything
  return null;
};